import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { Icons } from '../../../component/icons/Icons';
import RestHttpClient from '../../../common/RestHttpClient';
import { useIdentityPolling } from './useIdentityPolling';
import { pushNotificationHandler } from '../../../common/PushNotification';

interface Props {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  onCanceled?: () => void;
  customerId?: number;
}

const confirmIdentityCheck = async (checkId: string, customerId: number): Promise<void> => {
  await RestHttpClient.completeIdentityCheck(checkId, customerId, undefined);
};

const cancelIdentityCheck = async (checkId: string, customerId?: number): Promise<void> => {
  await RestHttpClient.cancelIdentityCheck(checkId, customerId);
};

export function IdentityCheckModal({ open, onClose, onCompleted, onCanceled, customerId }: Props) {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Use our new hook
  const { data, loading } = useIdentityPolling(customerId, open);

  const status = data?.status?.toLowerCase();
  const isAccepted = status === 'accepted' || status === 'completed';
  const nameCheckVerified = data?.details?.nameCheckVerified;

  const formatValue = (value: unknown): string => {
    if (value === undefined || value === null || value === '') return '-';
    if (value instanceof Date) return value.toLocaleDateString('de-DE');
    return String(value);
  };

  const handleConfirm = async () => {
    if (!data?.checkId || !customerId) return;
    setIsProcessing(true);
    try {
      await confirmIdentityCheck(data.checkId, customerId);
      onClose();
      onCompleted?.();
      pushNotificationHandler.publish(t('customers.identityCheck.confirmSuccess'), 'success');
    } catch (error) {
      console.error('Failed to confirm identity check:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setShowCancelConfirm(true);
  };

  const handleConfirmCancel = async () => {
    if (!data?.checkId) return;
    setShowCancelConfirm(false);
    setIsProcessing(true);
    try {
      await cancelIdentityCheck(data.checkId, customerId);
      onCanceled?.();
      onClose();
    } catch (error) {
      console.error('Failed to cancel identity check:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '.MuiDialogTitle-root': {
          pt: 0,
        },
      }}
    >
      <Box sx={{ textAlign: 'right' }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{ p: 0, height: '32px', width: '32px', minWidth: 'unset' }}
          aria-label="close"
        >
          {Icons.clear()}
        </Button>
      </Box>
      <DialogTitle>
        <Typography variant="h6">{t('customers.identityCheck.title')}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !data && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('customers.identityCheck.loading')}
            </Typography>
          </Box>
        )}

        {data && !isAccepted && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('customers.identityCheck.waitingForAcceptance')}
            </Typography>
            {data.checkId && (
              <Typography variant="caption" sx={{ mt: 1 }} color="text.secondary">
                {t('customers.identityCheck.checkId')}: {data.checkId}
              </Typography>
            )}
          </Box>
        )}

        {data && isAccepted && (
          <Box py={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              {nameCheckVerified === false ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningAmberIcon sx={{ color: 'warning.main' }} />
                  <Typography variant="h6" color="warning.main">
                    {t('customers.identityCheck.nameCheckFailed')}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="h6" color="success.main">
                  {data.status.toLowerCase() === 'completed'
                    ? t('customers.identityCheck.completed')
                    : t('customers.identityCheck.accepted')}
                </Typography>
              )}
            </Box>

            {/* Top Section: KontoCheck and Bankkonto side by side */}
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('customers.identityCheck.sections.kontoCheck')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      {t('customers.identityCheck.labels.orderNumber')}
                    </Typography>
                    <Typography variant="body1">{formatValue(data.checkId)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('customers.identityCheck.labels.orderDate')}
                    </Typography>
                    <Typography variant="body1">{formatValue(data.checkDate)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('customers.identityCheck.labels.validUntil')}
                    </Typography>
                    <Typography variant="body1">{formatValue(data.expirationDate)}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('customers.identityCheck.sections.bankAccount')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('customers.identityCheck.labels.accountHolder')}
                    </Typography>
                    <Typography variant="body1">
                      {formatValue(data.details?.bankAccount?.accountHolder)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('customers.identityCheck.labels.iban')}
                    </Typography>
                    <Typography variant="body1">
                      {formatValue(data.details?.bankAccount?.iban)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('customers.identityCheck.labels.bic')}
                    </Typography>
                    <Typography variant="body1">
                      {formatValue(data.details?.bankAccount?.bic)}
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography variant="caption" color="text.secondary">
                      {t('customers.identityCheck.labels.bankName')}
                    </Typography>
                    <Typography variant="body1">
                      {formatValue(data.details?.bankAccount?.bankName)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('customers.identityCheck.labels.bankCountry')}
                    </Typography>
                    <Typography variant="body1">
                      {formatValue(data.details?.bankAccount?.bankCountry)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      {data && isAccepted && (
        <DialogActions>
          <Button
            onClick={handleCancel}
            variant="outlined"
            color="secondary"
            disabled={isProcessing}
          >
            {t('customers.identityCheck.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={
              isProcessing ||
              data.status.toLowerCase() === 'completed' ||
              nameCheckVerified === false
            }
          >
            {t('customers.identityCheck.confirm')}
          </Button>
        </DialogActions>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('customers.identityCheck.cancelConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('customers.identityCheck.cancelConfirmMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelConfirm(false)} variant="outlined" color="secondary">
            {t('customers.identityCheck.cancelConfirmNo')}
          </Button>
          <Button onClick={handleConfirmCancel} variant="contained" color="primary">
            {t('customers.identityCheck.cancelConfirmYes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
